import React, { useRef, useCallback, useState, useEffect } from "react";
import { GoogleMap, useLoadScript, OverlayView } from "@react-google-maps/api";
import { Ship } from "lucide-react";
import portIcon from "../../assets/images/port-icon2.png";

const getStatusColor = (vesselStatus) => {
  switch (vesselStatus) {
    case "Anchorage":
      return "#cc9b09ff";
    case "Sailing":
      return "#178635ff";
    case "At Port":
      return "#2b6eebff";
    default:
      return "#eb2b35ff";
  }
};

const getStatusGlowColor = (vesselStatus) => {
  switch (vesselStatus) {
    case "Anchorage":
      return "#b68a09ff";
    case "Sailing":
      return "#0e752aff";
    case "At Port":
      return "#2c549eff";
    default:
      return "#eb2b35ff";
  }
};

const VesselMap = ({ lastVoyage }) => {
  console.log("lastVoyage", lastVoyage);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const mapRef = useRef(null);
  const [locations, setLocations] = useState({
    start: null,
    current: null,
    end: null,
  });
  const [center, setCenter] = useState({ lat: 20, lng: 0 }); // Default center point
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isLoaded) return;

    const geocoder = new window.google.maps.Geocoder();

    const geocodeAll = async () => {
      try {
        // If no voyage data, set loading to false and return
        if (!lastVoyage || lastVoyage.status === "error") {
          setLocations({
            start: null,
            current: null,
            end: null
          });
          setLoading(false);
          return;
        }

        // Handle decimal format if present
        const getDecimalValue = (decimalObj) => {
          if (decimalObj === null || decimalObj === undefined) return null;
          return typeof decimalObj === "object" && decimalObj.$numberDecimal
            ? parseFloat(decimalObj.$numberDecimal)
            : parseFloat(decimalObj);
        };

        const newLocations = {
          start: null,
          current: null,
          end: null
        };

        // Process current location if available
        const currentLat = getDecimalValue(lastVoyage.currentLatitude);
        const currentLng = getDecimalValue(lastVoyage.currentLongitude);
        
        if (currentLat !== null && currentLng !== null && 
            !isNaN(currentLat) && !isNaN(currentLng)) {
          newLocations.current = {
            lat: currentLat,
            lng: currentLng,
            name: "Current Position"
          };
        }

        // Process start port if available
        if (lastVoyage.fromPort) {
          const startPort = await geocodePlace(geocoder, lastVoyage.fromPort);
          if (startPort) {
            newLocations.start = {
              lat: startPort.lat,
              lng: startPort.lng,
              name: lastVoyage.fromPort
            };
          }
        }

        // Process end port if available
        if (lastVoyage.toPort) {
          const endPort = await geocodePlace(geocoder, lastVoyage.toPort);
          if (endPort) {
            newLocations.end = {
              lat: endPort.lat,
              lng: endPort.lng,
              name: lastVoyage.toPort
            };
          }
        }

        setLocations(newLocations);
        
        // Set center to current location if available, otherwise use first available point
        const firstValidLocation = Object.values(newLocations).find(Boolean);
        if (firstValidLocation) {
          setCenter(firstValidLocation);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error loading map data:", error);
        setError(error.message);
        setLocations({
          start: null,
          current: null,
          end: null
        });
        setLoading(false);
      }
    };

    geocodeAll();
  }, [isLoaded, lastVoyage]);

  const geocodePlace = (geocoder, address) => {
    return new Promise((resolve) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results[0]) {
          resolve({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
            name: address,
          });
        } else {
          console.warn(`Geocode failed for ${address}: ${status}`);
          resolve(null);
        }
      });
    });
  };

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
    map.setOptions({ scrollwheel: false });
    map.addListener("mousedown", () => {
      map.setOptions({ scrollwheel: true });
    });
    map.addListener("mouseout", () => {
      map.setOptions({ scrollwheel: false });
    });
  }, []);

  const mapStyle = { width: "100%", height: "350px", borderRadius: "12px" };

  if (loadError) return <div className="map-error">Error loading maps</div>;
  if (!isLoaded) return <div className="map-loading">Loading Maps...</div>;
  if (error) return <div className="map-error">Error: {error}</div>;

  // Only include points that have valid coordinates
  const points = [locations.start, locations.current, locations.end]
    .filter(Boolean)
    .filter(point => 
      point.lat !== null && 
      point.lng !== null && 
      !isNaN(point.lat) && 
      !isNaN(point.lng)
    );

  return (
    <div className="relative">
      <GoogleMap
        mapContainerStyle={mapStyle}
        center={center}
        zoom={3}
        onLoad={handleMapLoad}
        options={{
          mapTypeId: "satellite",
          restriction: {
            latLngBounds: {
              north: 60,
              south: -45,
              west: -160,
              east: 150,
            },
          },
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          zoomControl: false,
          rotateControl: false,
          scaleControl: false,
          gestureHandling: "auto",
          draggable: true,
          scrollwheel: false,
          disableDoubleClickZoom: false,
          keyboardShortcuts: false,
        }}
      >
        {points.map((pt, idx) => {
          const isCurrent = pt === locations.current;

          return (
            <OverlayView
              key={idx}
              position={{ lat: pt.lat, lng: pt.lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div className="group relative flex items-center gap-2 cursor-pointer">
                <div className="relative flex items-center justify-center">
                  {isCurrent && (
                    <div className="absolute w-10 h-10 rounded-full bg-cyan-400 opacity-30" />
                  )}
                  <div
                    className={`z-10 flex items-center justify-center bg-[#1a325d] p-1.5 rounded-full shadow-md ${
                      isCurrent ? "" : "drop-shadow-[0_0_6px_#ffff]"
                    }`}
                    style={{
                      backgroundColor: isCurrent
                        ? getStatusColor(lastVoyage?.vessel?.status)
                        : "#1a325d",
                      boxShadow: isCurrent
                        ? `0 0 6px ${getStatusGlowColor(
                            lastVoyage?.vessel?.status
                          )}`
                        : "none",
                    }}
                  >
                    {isCurrent ? (
                      <Ship className="text-white w-4 h-4" />
                    ) : (
                      <img src={portIcon} alt="Port Icon" className="w-4 h-4" />
                    )}
                  </div>
                </div>

                <span className="text-white text-[12px] whitespace-nowrap bg-[#1a325d] px-3 py-1 rounded">
                  {pt.name}
                </span>

                <div className="absolute -top-16 left-8 -translate-x-1/2 scale-95 opacity-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-200 ease-out flex flex-col items-center z-50">
                  <div className="bg-black/60 text-white text-[12px] px-4 py-2 rounded shadow-md whitespace-nowrap">
                    <p className="font-bold whitespace-nowrap mb-2">
                      {isCurrent ? "Current Position" : "Port Location"}
                    </p>
                    <p>Location: {pt.name}</p>
                    <p>
                      Latitude:{" "}
                      {typeof pt.lat === "number" ? pt.lat.toFixed(4) : "N/A"}
                    </p>
                    <p>
                      Longitude:{" "}
                      {typeof pt.lng === "number" ? pt.lng.toFixed(4) : "N/A"}
                    </p>
                  </div>
                  <div className="w-2 h-2 bg-black/60 rotate-45 mt-[-4px]" />
                </div>
              </div>
            </OverlayView>
          );
        })}
      </GoogleMap>
      {!loading && points.length === 0 && (
        <div className="absolute top-2 left-2 bg-blue-500/80 text-white text-xs px-2 py-1 rounded">
          No location data available
        </div>
      )}
    </div>
  );
};

export default VesselMap;
